<script setup lang="ts">
import { computed, ref, onMounted, onBeforeUnmount, watch, PropType } from 'vue';
import { PhxsgButton } from '@sg/src/View/Components/Atoms/Button';
import { PhxsgRichText } from '@sg/src/View/Components/Atoms/RichText';

interface Localisation {
  code: string;
  url: string;
  country_code: string | number;
  select_label: string;
}

const props = defineProps({
  localisations: {
    type: Array as PropType<Localisation[]>,
    required: true,
  },
  countries: {
    type: Object,
    required: true,
  },
});

const geolocatedCode = ref<string | null>(null);
const selectedUrl = ref<string>('');
const bannerRef = ref<HTMLElement | null>(null);

const geolocatedCountry = computed(() => {
  if (!geolocatedCode.value) return null;

  return props.countries[geolocatedCode.value] ?? null;
});

const filteredLocalisations = computed<Localisation[]>(() => {
  if (!geolocatedCode.value) return [];
  return props.localisations.filter((loc) => String(loc.country_code).toUpperCase() === geolocatedCode.value);
});

const hasLocalisations = computed(() => {
  return filteredLocalisations.value.length > 0;
});

const updateGeolocatedCode = () => {
  const code = window.Phoenix?.geolocatedCountryCode;
  if (!code) return;

  geolocatedCode.value = code.toUpperCase();
};

onMounted(() => {
  updateGeolocatedCode();

  if (bannerRef.value) {
    bannerRef.value.addEventListener('countryPropositionLocalisationsVisible', updateGeolocatedCode);
  }
});

onBeforeUnmount(() => {
  if (bannerRef.value) {
    bannerRef.value.removeEventListener('countryPropositionLocalisationsVisible', updateGeolocatedCode);
  }
});

/* check if the user language is in the list of localisations propositions */
watch(filteredLocalisations, (list) => {
  if (!Array.isArray(list) || list.length === 0) {
    selectedUrl.value = '';
    return;
  }

  const userLanguage = (navigator.language || '').split('-')[0];
  const match = list.find((loc) => loc.code === userLanguage);

  selectedUrl.value = match ? match.url : list[0].url;
});
</script>

<template>
  <div id="country-proposition-banner" ref="bannerRef" class="promo-banner collapse container">
    <div class="promo-banner__inner promo-banner--actions">
      <phxsg-rich-text>
        <span v-if="geolocatedCountry">{{
          $t('CountryProposition.visitingFrom', { currentCountry: geolocatedCountry })
        }}</span>
        <span v-else>{{ $t('CountryProposition.noGeolocatedCountry') }}</span>
      </phxsg-rich-text>

      <div
        v-if="hasLocalisations"
        :class="['input-wrapper', { 'input-wrapper-select': filteredLocalisations.length > 1 }]"
      >
        <select
          v-if="filteredLocalisations.length > 1"
          v-model="selectedUrl"
          class="input input--select input--sm promo-banner__language input-wrapper__input"
        >
          <option
            v-for="({ country_code, url, select_label }, index) in filteredLocalisations"
            :key="`${country_code}-${index}`"
            :data-code="country_code"
            :value="url"
          >
            {{ select_label }}
          </option>
        </select>
      </div>

      <phxsg-button
        :use="hasLocalisations && selectedUrl ? 'a' : 'button'"
        variant="secondary"
        size="sm"
        full
        class="promo-banner__submit"
        v-bind="
          hasLocalisations && selectedUrl
            ? { href: selectedUrl }
            : { 'data-panel-open': 'country-selector-panel', type: 'button' }
        "
      >
        {{ $t('CountryProposition.changeLocation') }}
      </phxsg-button>

      <div class="promo-banner__close">
        <phxsg-button
          icon-only
          icon-id="close"
          data-toggle="collapse"
          data-target="#country-proposition-banner"
          aria-expanded="true"
          :label="$t('Accessibility.modal.close')"
        />
      </div>
    </div>
  </div>
</template>
